import {
  formatDateValue,
  getMillisecondsUntilNextQuarterHour,
} from "../../utils/date";
import {
  canPrepareAutomationExecutionCommunication,
  canSendAutomationExecutionNow,
  getAutomationExecutionModeStatusLabel,
  getAutomationExecutionModeStatusBackground,
  getContentPreparationText,
  isFixtureExecution,
  needsCommunicationPublishing,
} from "../automation/executions/automationExecutionRules";
import { mapAutomationExecutionSummary } from "../automation/executions/automationExecutionRows";
import { formatAutomationRateValue } from "../automation/automationFormat";

const DashboardStatus = Object.freeze({
  Empty: "empty",
  Error: "error",
  Loading: "loading",
  Ready: "ready",
});

function createAutomationDashboardLink(to) {
  return { to };
}

function getCurrentSeasonMeta(source) {
  if (!source) {
    return [];
  }

  return [
    formatDateValue(source.Start),
    formatDateValue(source.End),
  ]
    .filter((item) => item)
    .slice(0, 2)
    .reduce((items, value, index, array) => {
      if (index === 0 && array.length === 2) {
        items.push({
          value: `${array[0]} - ${array[1]}`,
        });
      } else if (array.length === 1) {
        items.push({ value });
      }

      return items;
    }, []);
}

function getNextFixtureValue(source) {
  if (source?.Name) {
    return source.Name;
  }

  return (
    formatDateValue(source?.Start, "DD/MM/YYYY HH:mm") || "-"
  );
}

function createMetricItem(id, label, value, meta = [], link = null) {
  return {
    id,
    label,
    value:
      typeof value !== "undefined" && value !== null ? String(value) : "-",
    link,
    meta,
  };
}

function getCurrentSeasonLink(source) {
  if (source?.Id) {
    return createAutomationDashboardLink(`/season?id=${source.Id}`);
  }

  return null;
}

function getNextFixtureLink(source) {
  if (source?.Id) {
    return createAutomationDashboardLink(`/fixture?id=${source.Id}`);
  }

  return null;
}

function getEventProfilesLink() {
  return createAutomationDashboardLink("/event-profiles");
}

function buildRecentRunItem(item, index) {
  const execution = mapAutomationExecutionSummary(item);
  const fixtureExecution = isFixtureExecution(execution);
  const communicationId = execution.communicationId;
  const requiresPublishing = needsCommunicationPublishing(execution);
  const modeStatus =
    execution.executionAutomationStatus ?? execution.automationStatus;

  return {
    id:
      execution.executionId ||
      `recent-run-${index}`,
    name: execution.automationName || "-",
    type: execution.automationType || "-",
    statusName: execution.statusName || "Completed",
    statusLabel: execution.statusLabel || "Completed",
    modeStatus: getAutomationExecutionModeStatusLabel(
      modeStatus
    ),
    modeStatusBackground: getAutomationExecutionModeStatusBackground(
      modeStatus
    ),
    communicationName: execution.communicationName || "-",
    communicationStatus:
      requiresPublishing
        ? "Needs publishing"
        : fixtureExecution && !communicationId
          ? "Not prepared"
          : "",
    communicationStatusBackground:
      requiresPublishing
        ? "bg-warning"
        : "bg-info",
    communicationLink: communicationId
      ? createAutomationDashboardLink(
          `/email-communication?id=${communicationId}`
        )
      : null,
    audienceName: execution.audienceName || "-",
    audienceLink: execution.audienceId
      ? createAutomationDashboardLink(
          `/audience?id=${execution.audienceId}`
        )
      : null,
    completedOn:
      formatDateValue(execution.completedOn, "DD/MM/YYYY HH:mm") || "-",
    lastError: execution.lastError || "",
    fixtureName: fixtureExecution ? execution.fixtureName : "",
    fixtureLink: execution.relatedEntityId
      ? createAutomationDashboardLink(
          `/fixture?id=${execution.relatedEntityId}`
        )
      : null,
    competitionName: execution.competitionName,
    teamName: execution.teamName,
    dueAt: formatDateValue(execution.dueAt, "DD/MM/YYYY HH:mm") || "-",
    emailsSent:
      item?.EmailsSent || item?.EmailsSent === 0
        ? String(item.EmailsSent)
        : "-",
    openRate: formatAutomationRateValue(item?.OpenRate),
    clickRate: formatAutomationRateValue(item?.ClickRate),
    clicksToOpenRate: formatAutomationRateValue(item?.ClicksToOpenRate),
    link: execution.automationId
      ? createAutomationDashboardLink(
          `/automation?id=${execution.automationId}`
        )
      : null,
  };
}

function buildUpcomingExecutionItem(item) {
  const execution = mapAutomationExecutionSummary(item);
  const fixtureExecution = isFixtureExecution(execution);
  const communicationId = execution.communicationId;
  const requiresPublishing = needsCommunicationPublishing(execution);
  const executionCommunicationStatusCode =
    execution.executionCommunicationStatusCode;
  const modeStatus =
    execution.executionAutomationStatus ?? execution.automationStatus;
  const updateCommunicationLink =
    requiresPublishing && communicationId
      ? createAutomationDashboardLink(
          `/email-communication?id=${communicationId}`
        )
      : null;
  const canPrepare =
    canPrepareAutomationExecutionCommunication(execution);

  return {
    id:
      execution.executionId ||
      `${execution.automationId}-${execution.occurrenceKey}`,
    automationId: execution.automationId,
    name: execution.automationName || "-",
    type: execution.automationType || "-",
    scheduleContext:
      execution.relatedEntityName || execution.scheduleMode || "-",
    fixtureName: fixtureExecution ? execution.fixtureName : "",
    fixtureId: execution.fixtureId,
    fixtureLink: execution.relatedEntityId
      ? createAutomationDashboardLink(
          `/fixture?id=${execution.relatedEntityId}`
        )
      : null,
    competitionName: execution.competitionName,
    teamName: execution.teamName,
    status: execution.status,
    statusName: execution.statusName,
    statusLabel: execution.statusLabel || execution.statusName || "-",
    modeStatus: getAutomationExecutionModeStatusLabel(
      modeStatus
    ),
    modeStatusBackground: getAutomationExecutionModeStatusBackground(
      modeStatus
    ),
    canCancel: execution.canCancel,
    dueAt: formatDateValue(execution.dueAt, "DD/MM/YYYY HH:mm") || "-",
    processingWindowStartsAt:
      formatDateValue(
        execution.processingWindowStartsAt,
        "DD/MM/YYYY HH:mm"
      ) ||
      "-",
    contentPreparationStartsAt:
      !fixtureExecution || communicationId
        ? ""
        : formatDateValue(
            execution.contentPreparationStartsAt,
            "DD/MM/YYYY HH:mm"
          ) || "",
    contentPreparationText:
      fixtureExecution && !communicationId
        ? getContentPreparationText(
            execution.contentPreparationStartsAt
          )
        : "",
    communicationId,
    communicationName: execution.communicationName || "-",
    executionCommunicationStatusCode,
    needsCommunicationPublishing: requiresPublishing,
    communicationStatus:
      requiresPublishing
        ? "Needs publishing"
        : fixtureExecution && !communicationId
          ? "Not prepared"
          : "",
    communicationStatusBackground:
      requiresPublishing
        ? "bg-warning"
        : "bg-info",
    communicationLink: communicationId
      ? createAutomationDashboardLink(
          `/email-communication?id=${communicationId}`
        )
      : null,
    audienceName: execution.audienceName || "-",
    audienceLink: execution.audienceId
      ? createAutomationDashboardLink(
          `/audience?id=${execution.audienceId}`
        )
      : null,
    updateCommunicationLink,
    lastError: execution.lastError,
    canPrepare,
    canSendNow: canSendAutomationExecutionNow({
      ...execution,
      executionCommunicationStatusCode,
      updateCommunicationLink,
      canPrepare,
      needsCommunicationPublishing: requiresPublishing,
    }),
    link: execution.automationId
      ? createAutomationDashboardLink(
          `/automation?id=${execution.automationId}`
        )
      : null,
  };
}

function isFixtureUpcomingExecution(execution) {
  return String(execution?.RelatedEntityType).toLowerCase() === "fixture";
}

function isTriggeredAutomationType(automationType) {
  return (
    String(automationType).toLowerCase() === "triggered" ||
    String(automationType) === "2"
  );
}

function getUpcomingExecutionsByType(data, propertyName, fixtureExecutions) {
  if (Array.isArray(data?.[propertyName])) {
    return data[propertyName];
  }

  if (!Array.isArray(data?.UpcomingExecutions)) {
    return [];
  }

  return data.UpcomingExecutions.filter(
    (execution) =>
      !isTriggeredAutomationType(execution?.AutomationType) &&
      isFixtureUpcomingExecution(execution) === fixtureExecutions
  );
}

function buildSeasonMetric(source) {
  return createMetricItem(
    "current-season",
    "Current season",
    source?.Name,
    getCurrentSeasonMeta(source),
    getCurrentSeasonLink(source)
  );
}

function buildFixtureMetric(source) {
  const meta = [];
  const fixtureStart = formatDateValue(
    source?.Start,
    "DD/MM/YYYY HH:mm"
  );

  if (fixtureStart) {
    meta.push({ value: fixtureStart });
  }

  return createMetricItem(
    "next-fixture",
    "Next fixture",
    getNextFixtureValue(source),
    meta,
    getNextFixtureLink(source)
  );
}

function buildTaggedFixturesMetric(taggedFixtures, totalFixtures) {
  const meta = [];
  const numericTaggedFixtures = Number(taggedFixtures);
  const numericTotalFixtures = Number(totalFixtures);
  const hasTaggedFixtures = Number.isFinite(numericTaggedFixtures);
  const hasTotalFixtures = Number.isFinite(numericTotalFixtures);

  if (hasTaggedFixtures && hasTotalFixtures && numericTotalFixtures > 0) {
    const untaggedFixtures = Math.max(
      numericTotalFixtures - numericTaggedFixtures,
      0
    );
    const taggedPercentage = Math.round(
      (numericTaggedFixtures / numericTotalFixtures) * 100
    );

    meta.push({
      value: `${untaggedFixtures} fixtures without event profiles.`,
    });
    meta.push({
      value: "Tagged fixtures are current-season fixtures linked to at least one event profile.",
    });

    return createMetricItem(
      "tagged-fixtures",
      "Fixtures tagged",
      `${numericTaggedFixtures} / ${numericTotalFixtures} (${taggedPercentage}%)`,
      meta
    );
  }

  if (hasTotalFixtures) {
    meta.push({
      value: `${numericTotalFixtures} total fixtures`,
    });
  }

  return createMetricItem(
    "tagged-fixtures",
    "Fixtures tagged",
    taggedFixtures,
    meta
  );
}

function buildUntaggedEventProfilesMetric(value) {
  return createMetricItem(
    "untagged-events",
    "Untagged event profiles",
    value,
    [
      {
        value:
          "Event profiles missing either a type or a season.",
      },
    ],
    getEventProfilesLink()
  );
}

function buildDashboardModel(data, retrievedAt = new Date()) {
  const upcomingDailyScheduledExecutions = getUpcomingExecutionsByType(
    data,
    "UpcomingDailyScheduledExecutions",
    false
  );
  const upcomingFixtureScheduledExecutions = getUpcomingExecutionsByType(
    data,
    "UpcomingFixtureScheduledExecutions",
    true
  );
  const recentRuns = Array.isArray(data?.RecentRuns)
    ? data.RecentRuns
    : [];

  return {
    status: DashboardStatus.Ready,
    lastUpdated: data?.LastUpdated || retrievedAt.toISOString(),
    trustMetrics: [
      buildSeasonMetric(data?.CurrentSeason),
      buildFixtureMetric(data?.NextFixture),
      buildTaggedFixturesMetric(
        data?.TaggedFixtures,
        data?.TotalFixtures
      ),
      buildUntaggedEventProfilesMetric(data?.UntaggedEventProfiles),
    ],
    recentRuns: recentRuns
      .slice(0, 3)
      .map((item, index) =>
          buildRecentRunItem(item, index)
        ),
    upcomingDailyScheduledExecutions: upcomingDailyScheduledExecutions
      .slice(0, 3)
      .map(buildUpcomingExecutionItem),
    upcomingFixtureScheduledExecutions: upcomingFixtureScheduledExecutions
      .slice(0, 3)
      .map(buildUpcomingExecutionItem),
  };
}

function createLoadingAutomationDashboardModel() {
  return {
    status: DashboardStatus.Loading,
    lastUpdated: null,
    trustMetrics: [],
    recentRuns: [],
    upcomingDailyScheduledExecutions: [],
    upcomingFixtureScheduledExecutions: [],
  };
}

function resolveLoaderResult(result) {
  if (result && result.data) {
    return buildDashboardModel(result.data);
  }

  if (result && result.response) {
    throw result;
  }

  if (result && result.status && result.status >= 400) {
    throw result;
  }

  if (!result) {
    return {
      ...createLoadingAutomationDashboardModel(),
      status: DashboardStatus.Empty,
    };
  }

  return buildDashboardModel(result);
}

function getErrorMessage(error) {
  if (!error) {
    return "The dashboard metrics could not be loaded.";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error.response && error.response.data) {
    if (typeof error.response.data === "string") {
      return error.response.data;
    }

    const message =
      error.response.data.message || error.response.data.Message;
    const detail =
      error.response.data.detail || error.response.data.Detail;

    if (message && detail) {
      return `${message} ${detail}`;
    }

    if (message) {
      return message;
    }

    if (detail) {
      return detail;
    }
  }

  return error.message || "The dashboard metrics could not be loaded.";
}

export {
  buildDashboardModel,
  createLoadingAutomationDashboardModel,
  DashboardStatus,
  getErrorMessage,
  getMillisecondsUntilNextQuarterHour,
  resolveLoaderResult,
};
